import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "../../../../utils/toast";
import {
  createPackages,
  getPackageFeaturesList,
} from "../../../../services/admin/adminServices";

const INT_MAX = 2147483647;

const INITIAL_FORM = {
  name: "",
  tagline: "",
  priceMonthly: "",
  priceYearly: "",
  validityDays: 30,
  sortOrder: 1,
  maxProperties: "",
  maxPropertiesUnlimited: false,
  maxTenants: "",
  maxTenantsUnlimited: false,
  maxCollections: "",
  maxCollectionsUnlimited: true,
  maxRooms: "",
  maxRoomsUnlimited: false,
  smsLimit: "",
  smsAfterPrice: "",
  waLimit: "",
  waAfterPrice: "",
  emailLimit: "",
  emailAfterPrice: "",
  storageGB: "",
  storageUnlimited: false,
  enablePG: false,
  enablePM: false,
  isActive: true,
  features: [],
};

export default function CreatePackage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [masterFeatures, setMasterFeatures] = useState([]);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const res = await getPackageFeaturesList();
      setMasterFeatures(res || []);
    } catch (err) {
      console.log("Failed to fetch features");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFeatureToggle = (id) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(id)
        ? prev.features.filter((f) => f !== id)
        : [...prev.features, id],
    }));
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!form.name) e.name = "Package name is required.";
    if (!form.priceMonthly) e.priceMonthly = "Monthly price is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        priceMonthly: Number(form.priceMonthly),
        priceYearly: Number(form.priceYearly) || 0,
        validityDays: Number(form.validityDays) || INT_MAX,
        sortOrder: Number(form.sortOrder) || 0,

        // ← -1 when unlimited instead of null
        maxProperties: form.maxPropertiesUnlimited
          ? -1
          : Number(form.maxProperties) || 0,
        maxTenants: form.maxTenantsUnlimited
          ? -1
          : Number(form.maxTenants) || 0,
        maxCollections: form.maxCollectionsUnlimited
          ? -1
          : Number(form.maxCollections) || 0,
        maxRooms: form.maxRoomsUnlimited ? -1 : Number(form.maxRooms) || 0,

        smsLimit: Number(form.smsLimit) || 0,
        smsAfterPrice: Number(form.smsAfterPrice) || 0,
        waLimit: Number(form.waLimit) || 0,
        waAfterPrice: Number(form.waAfterPrice) || 0,
        emailLimit: Number(form.emailLimit) || 0,
        emailAfterPrice: Number(form.emailAfterPrice) || 0,
        storageGB: form.storageUnlimited
          ? INT_MAX
          : Number(form.storageGB) || 0,

        enablePG: form.enablePG,
        enablePM: form.enablePM,
        isActive: form.isActive === "true" || form.isActive === true,

        // ← features array with selected masterFeatureIds e.g. [1, 2, 3]
        features: form.features,
        isFeatured: true,
      };

      const res = await createPackages(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        navigate("/admin/package-list");
      } else {
        toast.error(res.message || "Failed to create package.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ── Reusable unlimited field ──
  const LimitField = ({ label, name, unlimitedKey, placeholder }) => (
    <div className="form-group">
      <label>{label}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="number"
          name={name}
          value={form[name]}
          onChange={handleChange}
          disabled={form[unlimitedKey]}
          placeholder={placeholder}
          style={{ flex: 1, opacity: form[unlimitedKey] ? 0.4 : 1 }}
        />
        <label
          style={{
            fontSize: 12,
            fontWeight: 400,
            whiteSpace: "nowrap",
            display: "flex",
            gap: 4,
            alignItems: "center",
          }}
        >
          <input
            type="checkbox"
            name={unlimitedKey}
            checked={form[unlimitedKey]}
            onChange={handleChange}
          />
          Unlimited
        </label>
      </div>
    </div>
  );

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Create Package</div>
            <div className="page-sub">Packages › Create New Plan</div>
          </div>
          <div className="topbar-right" />
        </div>

        <div className="content" id="content-area">
          <div className="card">
            <div className="warn-box">
              Fill in the details below to create a new subscription package.
              Once created, it will be visible to property owners on their
              subscription page.
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Package Name *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Starter, Growth, Pro"
                  style={errors.name ? { borderColor: "#dc2626" } : {}}
                />
                {errors.name && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {errors.name}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Tagline</label>
                <input
                  type="text"
                  name="tagline"
                  value={form.tagline}
                  onChange={handleChange}
                  placeholder="Perfect for small properties"
                />
              </div>
              <div className="form-group">
                <label>Monthly Price (₹) *</label>
                <input
                  type="number"
                  name="priceMonthly"
                  value={form.priceMonthly}
                  onChange={handleChange}
                  placeholder="999"
                  style={errors.priceMonthly ? { borderColor: "#dc2626" } : {}}
                />
                {errors.priceMonthly && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {errors.priceMonthly}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Yearly Price (₹)</label>
                <input
                  type="number"
                  name="priceYearly"
                  value={form.priceYearly}
                  onChange={handleChange}
                  placeholder="9999"
                />
              </div>
              <div className="form-group">
                <label>Validity (Days)</label>
                <input
                  type="number"
                  name="validityDays"
                  value={form.validityDays}
                  onChange={handleChange}
                  placeholder="30"
                />
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  name="sortOrder"
                  value={form.sortOrder}
                  onChange={handleChange}
                  placeholder="1"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="isActive"
                  value={form.isActive}
                  onChange={handleChange}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
              <div className="form-group"></div>
              <div className="form-group">
                <label>Enable Payment Gateway</label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 13,
                    fontWeight: 400,
                    marginTop: 6,
                  }}
                >
                  <input
                    type="checkbox"
                    name="enablePG"
                    checked={form.enablePG}
                    onChange={handleChange}
                  />
                  Enable PG
                </label>
              </div>
              <div className="form-group">
                <label>Enable Payment Management</label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 13,
                    fontWeight: 400,
                    marginTop: 6,
                  }}
                >
                  <input
                    type="checkbox"
                    name="enablePM"
                    checked={form.enablePM}
                    onChange={handleChange}
                  />
                  Enable PM
                </label>
              </div>
            </div>

            <hr className="section-divider" />
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
              Property & Usage Limits
            </div>

            <div className="form-grid">
              <LimitField
                label="Allowed Properties"
                name="maxProperties"
                unlimitedKey="maxPropertiesUnlimited"
                placeholder="5"
              />
              <LimitField
                label="Max Tenants"
                name="maxTenants"
                unlimitedKey="maxTenantsUnlimited"
                placeholder="100"
              />
              <LimitField
                label="Rent Collections"
                name="maxCollections"
                unlimitedKey="maxCollectionsUnlimited"
                placeholder="200"
              />
              <LimitField
                label="Max Rooms"
                name="maxRooms"
                unlimitedKey="maxRoomsUnlimited"
                placeholder="200"
              />

              <div className="form-group">
                <label>SMS Limit / month</label>
                <input
                  type="number"
                  name="smsLimit"
                  value={form.smsLimit}
                  onChange={handleChange}
                  placeholder="1000"
                />
              </div>
              <div className="form-group">
                <label>SMS After Limit Price (₹)</label>
                <input
                  type="number"
                  name="smsAfterPrice"
                  value={form.smsAfterPrice}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label>WhatsApp Limit / month</label>
                <input
                  type="number"
                  name="waLimit"
                  value={form.waLimit}
                  onChange={handleChange}
                  placeholder="2000"
                />
              </div>
              <div className="form-group">
                <label>WhatsApp After Limit Price (₹)</label>
                <input
                  type="number"
                  name="waAfterPrice"
                  value={form.waAfterPrice}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label>Email Limit / month</label>
                <input
                  type="number"
                  name="emailLimit"
                  value={form.emailLimit}
                  onChange={handleChange}
                  placeholder="500"
                />
              </div>
              <div className="form-group">
                <label>Email After Limit Price (₹)</label>
                <input
                  type="number"
                  name="emailAfterPrice"
                  value={form.emailAfterPrice}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Storage (GB)</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="number"
                    name="storageGB"
                    value={form.storageGB}
                    onChange={handleChange}
                    disabled={form.storageUnlimited}
                    placeholder="10"
                    style={{
                      flex: 1,
                      opacity: form.storageUnlimited ? 0.4 : 1,
                    }}
                  />
                </div>
              </div>
            </div>

            <hr className="section-divider" />
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
              Included Features
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                marginBottom: 16,
              }}
            >
              {masterFeatures.map((f) => (
                <label
                  key={f.masterFeatureId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 12,
                    fontWeight: 400,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.features.includes(f.masterFeatureId)}
                    onChange={() => handleFeatureToggle(f.masterFeatureId)}
                  />
                  {f.featureName}
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Creating..." : "Create Package"}
              </button>
              <button className="btn" onClick={handleReset} disabled={loading}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
